import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Normalize route for permission matching - strips /admin prefix, handles aliases
  const normalizeRoute = (route) => {
    if (!route) return '';
    let r = route.toLowerCase().replace(/\/+$/, '');
    // Strip /admin/ prefix for matching
    if (r.startsWith('/admin/')) r = '/' + r.substring(7);
    return r;
  };

  const hasPagePermission = (pageRoute, permission = 'can_view') => {
    if (!user?.permissions) return false;
    // Super Admin always has access
    if (user.role_id === 1) return true;
    const normalizedInput = normalizeRoute(pageRoute);
    return user.permissions.some(page => {
      const normalizedDb = normalizeRoute(page.page_route);
      return normalizedDb === normalizedInput && page[permission];
    });
  };

  // Get all permission flags for a specific page route
  const getPagePermissions = (pageRoute) => {
    if (!user?.permissions) return { can_list: false, can_view: false, can_add: false, can_edit: false, can_delete: false, can_download: false };
    // Super Admin always has full access
    if (user.role_id === 1) return { can_list: true, can_view: true, can_add: true, can_edit: true, can_delete: true, can_download: true };
    const normalizedInput = normalizeRoute(pageRoute);
    const perm = user.permissions.find(page => normalizeRoute(page.page_route) === normalizedInput);
    if (!perm) return { can_list: false, can_view: false, can_add: false, can_edit: false, can_delete: false, can_download: false };
    return {
      can_list: !!perm.can_list,
      can_view: !!perm.can_view,
      can_add: !!perm.can_add,
      can_edit: !!perm.can_edit,
      can_delete: !!perm.can_delete,
      can_download: !!perm.can_download,
    };
  };

  useEffect(() => {
    // Validate token on mount
    const validateToken = async () => {
      try {
        if (token) {
          const response = await authService.validateToken();
          setUser(response.data.user);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        localStorage.removeItem('token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await authService.login({ email, password });
      const { token: newToken, user: userData, redirect } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);

      // redirect is derived from the user's department, e.g. '/bm/tickets'
      return { ...userData, redirect };
    } catch (error) {
      throw error.response?.data?.error || 'Login failed';
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await authService.register({ name, email, password });
      return response.data.user;
    } catch (error) {
      throw error.response?.data?.error || 'Registration failed';
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token,
    isAdmin: user?.role_id === 2 ||user?.role_id === 1 || user?.role === 'admin',
    hasPagePermission,
    getPagePermissions,
    permissions: user?.permissions || [],
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
